import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { useGetApi, useIsOnline } from "../../../hooks";
import { useDispatch, useSelector } from "../../../redux/hooks";
import {
  clearNMEAAmendModalData,
  setNMEAAmendModalData,
} from "../../../redux/slices/generatorFeaturesSlice";
import { getYachtId } from "../../../utils";
import { type ISection } from "src/interfaces";
import type { INMEAAmendModalData } from "src/interfaces/Modals";
import {
  deriveNMEAId,
  getClockHourKey,
  getRawNMEARowTimeLT,
  getSectionScopedNMEAPool,
  isNMEADataRowConsumedByTime,
  mergeNMEADataSources,
  normalizeTimeLT,
} from "./NMEAAutomationModal.utils";
import { isLegacyNMEAPreviewLogbook } from "./nmeaPreviewStyling";

// ─── Types ───────────────────────────────────────────────────────────────────

export type { INMEAAmendModalData };

interface IAmendCandidate {
  sectionIndex: number;
  rowIndex: number;
  nmeaDataRow: Record<string, any>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const mapper = (data: any): Array<Record<string, string>> => data.data;

const getAllColValues = (row: any): any[] => {
  const cols = row.cols ? Object.values(row.cols) : [];
  const subrowCols =
    row.subrows?.flatMap((sr: any) => Object.values(sr.cols ?? {})) ?? [];
  return [...cols, ...subrowCols];
};

const rowHasNoNMEAId = (row: any): boolean =>
  getAllColValues(row).every((col: any) => !col?.NMEAId);

const rowHasNMEAColumns = (row: any): boolean =>
  getAllColValues(row).some((col: any) => !!col?.NMEA);

/** True if at least one row in any section needs NMEA data applied. */
const hasAmendableRows = (sections: ISection[]): boolean =>
  sections.some(section =>
    section.rows.some(row => rowHasNMEAColumns(row) && rowHasNoNMEAId(row)),
  );

const getRowTimeLT = (row: any): string | null => {
  const timeCol = getAllColValues(row).find(
    (col: any) => col?.NMEA === "_ROW_DATE_TIME" || col?.NMEA === "ROW_TIME",
  );
  if (!timeCol) return null;

  if (typeof timeCol.value === "object" && timeCol.value?.time) {
    return timeCol.value.time as string;
  }

  if (typeof timeCol.value === "string" && timeCol.value) {
    const match = (timeCol.value as string).match(/(\d{2}:\d{2})/);
    return match ? match[1] : timeCol.value;
  }

  return null;
};

const extractDateOnly = (entryDate: string): string | null => {
  const match = entryDate.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

/** Combines the row's local time with the entry date. */
const buildRowDateTime = (
  entryDate: string | null,
  timeLT: string,
): Date | null => {
  if (!entryDate) return null;

  const dateOnly = extractDateOnly(entryDate);
  if (!dateOnly) return null;

  const normalized = timeLT.match(/(\d{2}:\d{2})/)?.[1] ?? timeLT;
  const date = new Date(`${dateOnly}T${normalized}:00`);
  return isNaN(date.getTime()) ? null : date;
};

const getHourEnd = (date: Date): Date => {
  const end = new Date(date);
  end.setHours(end.getHours() + 1, 0, 0, 0);
  return end;
};

/** Parses an NMEA data row's date-time, preferring the LT field over the raw one. */
const parseNMEADateTime = (
  dataRow: Record<string, any>,
  entryDate?: string | null,
): Date | null => {
  const ltString = dataRow._ROW_DATE_TIME_LT?.replace(/\s*LT$/, "");
  if (ltString) {
    const parsed = new Date(ltString);
    if (!isNaN(parsed.getTime())) return parsed;
  }

  if (dataRow._ROW_DATE_TIME) {
    const parsed = new Date(dataRow._ROW_DATE_TIME);
    if (!isNaN(parsed.getTime())) return parsed;
  }

  const timeLT = getRawNMEARowTimeLT(dataRow);
  if (timeLT && entryDate) {
    return buildRowDateTime(entryDate, timeLT);
  }

  return null;
};

/**
 * Finds the NMEA data row closest to, but strictly after, the manual row time,
 * and only within the same hour.
 */
const findBestNMEAMatch = (
  rowTimeLT: string,
  nmeaData: Array<Record<string, any>>,
  entryDate: string | null,
): Record<string, any> | null => {
  const rowDateTime = buildRowDateTime(entryDate, rowTimeLT);
  if (!rowDateTime) return null;

  const hourEnd = getHourEnd(rowDateTime);

  let bestMatch: Record<string, any> | null = null;
  let bestDelta = Infinity;

  nmeaData.forEach(dataRow => {
    const nmeaDateTime = parseNMEADateTime(dataRow, entryDate);
    if (!nmeaDateTime) return;
    if (nmeaDateTime <= rowDateTime) return;
    if (nmeaDateTime >= hourEnd) return;

    const delta = nmeaDateTime.getTime() - rowDateTime.getTime();
    if (delta < bestDelta) {
      bestDelta = delta;
      bestMatch = dataRow;
    }
  });

  return bestMatch;
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useNMEAAmendCheck = () => {
  const dispatch = useDispatch();
  const { lbeidOrPartId, logbookId } = useParams();
  const isOnline = useIsOnline();

  const { lbentry } = useSelector(s => s.generatorSlice);
  const id = useSelector(state => state.generatorSlice.latestEntry.id);
  const legacyPreviewEnabled = isLegacyNMEAPreviewLogbook(logbookId);

  // Guard: run exactly once per page load
  const hasRunRef = useRef(false);

  const nmeaApiBody =
    id && String(id).length > 0
      ? {
          params: {
            yachtid: getYachtId(),
            lbid: logbookId,
          },
        }
      : undefined;

  const { refetch, isLoading } = useGetApi({
    request: "get-NMEA-data",
    body: nmeaApiBody,
    mapper,
    fetchOnInit: false,
  });

  useEffect(() => {
    if (legacyPreviewEnabled) {
      dispatch(clearNMEAAmendModalData());
      return;
    }

    if (
      hasRunRef.current ||
      !lbentry.NMEA ||
      !isOnline ||
      !id ||
      String(id).length === 0 ||
      isLoading
    ) {
      return;
    }

    hasRunRef.current = true;

    // Don't bother fetching NMEA data if nothing needs amending anyway
    if (!hasAmendableRows(lbentry.sections)) return;

    const entryDate: string | null = lbentry?.entrydate ?? null;

    const run = async () => {
      let apiRows: Array<Record<string, any>> = [];

      try {
        const result = await refetch();
        apiRows = (result as Array<Record<string, any>>) ?? [];
      } catch (err) {
        console.error("[useNMEAAmendCheck] Failed to fetch NMEA data:", err);
        return;
      }

      const nmeaData = mergeNMEADataSources(apiRows, lbentry.NMEAData);
      if (nmeaData.length === 0) return;

      const claimedNMEAIds = new Set<string>();
      const claimedHourKeys = new Set<string>();
      const candidates: IAmendCandidate[] = [];

      lbentry.sections.forEach((section, sectionIndex) => {
        const sectionHasNMEACols = section.rows.some(r => rowHasNMEAColumns(r));
        if (!sectionHasNMEACols) return;

        const amendableRows = section.rows
          .map((row, rowIndex) => ({
            row,
            rowIndex,
            rowTimeLT: getRowTimeLT(row),
          }))
          .filter(
            item =>
              rowHasNoNMEAId(item.row) &&
              item.rowTimeLT &&
              rowHasNMEAColumns(item.row),
          )
          .sort((a, b) => {
            const timeA = normalizeTimeLT(a.rowTimeLT) ?? "";
            const timeB = normalizeTimeLT(b.rowTimeLT) ?? "";
            return timeA.localeCompare(timeB);
          });

        amendableRows.forEach(({ rowIndex, rowTimeLT }) => {
          if (!rowTimeLT) return;

          const hourKey = getClockHourKey(entryDate, rowTimeLT);
          const sectionHourKey =
            hourKey !== null ? `${sectionIndex}-${hourKey}` : null;

          if (sectionHourKey && claimedHourKeys.has(sectionHourKey)) return;

          const sectionPool = getSectionScopedNMEAPool(
            sectionIndex,
            nmeaData,
          ).filter(
            row =>
              !claimedNMEAIds.has(deriveNMEAId(row)) &&
              !isNMEADataRowConsumedByTime(
                row,
                lbentry.sections,
                sectionIndex,
              ),
          );

          const bestMatch = findBestNMEAMatch(
            rowTimeLT,
            sectionPool,
            entryDate,
          );
          if (!bestMatch) return;

          const matchId = deriveNMEAId(bestMatch);
          claimedNMEAIds.add(matchId);
          if (sectionHourKey) claimedHourKeys.add(sectionHourKey);

          candidates.push({ sectionIndex, rowIndex, nmeaDataRow: bestMatch });
        });
      });

      if (candidates.length === 0) return;

      dispatch(
        setNMEAAmendModalData({
          candidates,
          rawNMEAData: nmeaData,
        }),
      );
    };

    void run();
  }, [dispatch, id, isLoading, isOnline, lbentry.NMEA, lbentry.NMEAData, lbentry.entrydate, lbentry.sections, legacyPreviewEnabled, logbookId, refetch]);
};
